import { useQuery } from '@tanstack/react-query';
import {
  WorkspaceMembersApi,
  type WorkspaceMemberWithUserDto,
} from '@up2stay/up2promo-api-client';
import { useEffect, useState } from 'react';

import { isActiveWorkspaceMember } from '@/core/lib/workspace-member-display';
import { workspaceMembersQueryKeys } from '@/core/query-keys/workspace-members-query-keys';

function useDebouncedValue<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

async function findMemberByUserId(
  workspaceId: string,
  userId: string,
): Promise<WorkspaceMemberWithUserDto | null> {
  let page = 1;
  const limit = 100;
  while (page <= 5) {
    const res = await WorkspaceMembersApi.findAll({
      path: { workspaceId },
      query: { page, limit },
    });
    const rows = res.data?.data ?? [];
    const found = rows.find((m) => m.userId === userId);
    if (found) return found;
    if (!res.data?.hasNextPage) break;
    page += 1;
  }
  return null;
}

export function useWorkspaceMemberSearch(
  workspaceId: string | undefined,
  options: { search: string; enabled: boolean },
) {
  const debouncedSearch = useDebouncedValue(options.search, 300);

  return useQuery({
    queryKey: workspaceMembersQueryKeys.search(workspaceId, debouncedSearch),
    queryFn: async () => {
      const res = await WorkspaceMembersApi.findAll({
        path: { workspaceId: workspaceId! },
        query: {
          page: 1,
          limit: 80,
          ...(debouncedSearch.trim() ? { search: debouncedSearch.trim() } : {}),
        },
      });
      return (res.data?.data ?? []).filter(isActiveWorkspaceMember);
    },
    enabled: options.enabled && !!workspaceId,
    staleTime: 30_000,
    retry: 1,
  });
}

export function useResolvedWorkspaceMember(
  workspaceId: string | undefined,
  userId: string | null | undefined,
) {
  const trimmed = userId?.trim() ?? '';

  return useQuery({
    queryKey: workspaceMembersQueryKeys.resolve(workspaceId, trimmed),
    queryFn: async () => {
      if (!workspaceId || !trimmed) return null;
      return findMemberByUserId(workspaceId, trimmed);
    },
    enabled: !!workspaceId && !!trimmed,
    staleTime: 60_000,
    retry: 1,
  });
}
